import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import useRequest from "../../hooks/useRequest";
import Breadcrumb from "../Breadcrumb/Breadcrumb";

const PREFIX = "OrderCancellation";
const KEYS = {
  termsEn: `${PREFIX}.terms-en`,
  termsAr: `${PREFIX}.terms-ar`,
  reasonsEn: `${PREFIX}.reasons-en`,
  reasonsAr: `${PREFIX}.reasons-ar`,
  feeType: `${PREFIX}.fee-type`,
  feeValue: `${PREFIX}.fee-value`,
  blockedStatuses: `${PREFIX}.blocked-statuses`,
  blockWhenShipped: `${PREFIX}.block-when-shipped`,
};

const ORDER_STATUSES = [
  { value: "request_submitted", label: "Request Submitted" },
  { value: "offer_received", label: "Offer Received" },
  { value: "order_confirmed", label: "Order Confirmed" },
  { value: "order_partially_cancelled", label: "Partially Cancelled" },
  { value: "order_canelled", label: "Order Cancelled" },
  { value: "order_delivered", label: "Order Delivered" },
  { value: "order_partially_returned", label: "Partially Returned" },
  { value: "order_returned", label: "Order Returned" },
];

const parseLines = (value = "") =>
  String(value)
    .split(/\n/)
    .map((item) => item.trim())
    .filter(Boolean);

const parseStatuses = (value = "") =>
  String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const sectionStyle = {
  background: "#fff",
  border: "1px solid #E4E6EF",
  borderRadius: "8px",
  padding: "20px 24px",
  marginBottom: "20px",
};

const sectionTitleStyle = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#181C32",
  marginBottom: "4px",
};

const sectionHelpStyle = {
  fontSize: "13px",
  color: "#7E8299",
  marginBottom: "16px",
};

const labelStyle = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#3F4254",
  marginBottom: "6px",
  display: "block",
};

const chipActive = {
  border: "1px solid #3699FF",
  background: "#EEF6FF",
  color: "#3699FF",
};

const chipIdle = {
  border: "1px solid #E4E6EF",
  background: "#F3F6F9",
  color: "#3F4254",
};

const ReasonListEditor = ({
  title,
  help,
  reasons,
  setReasons,
  placeholder,
  dir = "ltr",
}) => {
  const [draft, setDraft] = useState("");

  const addReason = () => {
    const next = draft.trim();
    if (!next) return;
    if (reasons.includes(next)) {
      toast.error("This reason already exists.");
      return;
    }
    setReasons([...reasons, next]);
    setDraft("");
  };

  const removeReason = (index) => {
    setReasons(reasons.filter((_, i) => i !== index));
  };

  return (
    <div className="col-lg-6 mb-5">
      <div style={sectionStyle} className="h-100">
        <div style={sectionTitleStyle}>{title}</div>
        <div style={sectionHelpStyle}>{help}</div>

        <div className="d-flex align-items-center mb-4">
          <input
            type="text"
            className="form-control"
            style={{ background: "#F3F6F9", borderColor: "#E4E6EF" }}
            placeholder={placeholder}
            value={draft}
            dir={dir}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addReason();
              }
            }}
          />
          <button
            type="button"
            className="btn btn-primary font-weight-bold ml-3"
            style={{ minWidth: "90px" }}
            onClick={addReason}
          >
            Add
          </button>
        </div>

        {reasons.length === 0 ? (
          <div
            className="text-center py-5"
            style={{
              background: "#F9F9FC",
              border: "1px dashed #E4E6EF",
              borderRadius: "6px",
              color: "#B5B5C3",
              fontSize: "13px",
            }}
          >
            No reasons added yet
          </div>
        ) : (
          <div>
            {reasons.map((reason, index) => (
              <div
                key={`${reason}-${index}`}
                className="d-flex align-items-center justify-content-between mb-2 px-3 py-3"
                dir={dir}
                style={{
                  background: "#F9F9FC",
                  border: "1px solid #EEF0F8",
                  borderRadius: "6px",
                }}
              >
                <div className="d-flex align-items-center">
                  <span
                    className="d-inline-flex align-items-center justify-content-center mr-3"
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background: "#E1F0FF",
                      color: "#3699FF",
                      fontSize: "11px",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </span>
                  <span style={{ fontSize: "14px", color: "#181C32" }}>
                    {reason}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-light-danger"
                  onClick={() => removeReason(index)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const OrderCancellation = () => {
  const { register, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      termsEn: "",
      termsAr: "",
      feeType: "none",
      feeValue: "0",
      blockWhenShipped: "true",
    },
  });

  const [settingsMeta, setSettingsMeta] = useState({});
  const [reasonsEn, setReasonsEn] = useState([]);
  const [reasonsAr, setReasonsAr] = useState([]);
  const [blockedStatuses, setBlockedStatuses] = useState([]);

  const { response, request } = useRequest();
  const { response: responseFetchSetting, request: requestFetchSetting } =
    useRequest();

  useEffect(() => {
    document.title = "Order Cancellation Setting - Easyspare";
    requestFetchSetting("GET", `setting/all-prefix/${PREFIX}`);
  }, []);

  useEffect(() => {
    if (!responseFetchSetting) return;
    const { prefixSettings = [] } = responseFetchSetting;
    const meta = {};

    prefixSettings.forEach((setting) => {
      meta[setting.key] = setting;
      const value = setting.selected || setting.value || "";

      if (setting.key === KEYS.termsEn) setValue("termsEn", value);
      if (setting.key === KEYS.termsAr) setValue("termsAr", value);
      if (setting.key === KEYS.feeType) setValue("feeType", value || "none");
      if (setting.key === KEYS.feeValue) setValue("feeValue", value || "0");
      if (setting.key === KEYS.blockWhenShipped)
        setValue("blockWhenShipped", value || "true");
      if (setting.key === KEYS.blockedStatuses)
        setBlockedStatuses(parseStatuses(value));
      if (setting.key === KEYS.reasonsEn) setReasonsEn(parseLines(value));
      if (setting.key === KEYS.reasonsAr) setReasonsAr(parseLines(value));
    });

    setSettingsMeta(meta);
  }, [responseFetchSetting, setValue]);

  useEffect(() => {
    if (response) {
      toast.success("Order cancellation settings updated successfully.");
    }
  }, [response]);

  const toggleStatus = (status) => {
    setBlockedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    );
  };

  const onSubmit = (data) => {
    if (!reasonsEn.length) {
      toast.error("Please add at least one English cancellation reason.");
      return;
    }

    const dataToSubmit = [
      { key: KEYS.termsEn, value: data.termsEn || "" },
      { key: KEYS.termsAr, value: data.termsAr || "" },
      { key: KEYS.reasonsEn, value: reasonsEn.join("\n") },
      { key: KEYS.reasonsAr, value: reasonsAr.join("\n") },
      { key: KEYS.feeValue, value: String(data.feeValue ?? "0") },
      { key: KEYS.blockedStatuses, value: blockedStatuses.join(",") },
    ];

    const otherData = [
      { key: KEYS.feeType, selected: data.feeType || "none" },
      {
        key: KEYS.blockWhenShipped,
        selected: data.blockWhenShipped || "true",
      },
    ];

    const filteredData = dataToSubmit.filter((item) => settingsMeta[item.key]);
    const filteredOther = otherData.filter((item) => settingsMeta[item.key]);

    const formData = new FormData();
    formData.append("imagesKey", JSON.stringify([]));
    formData.append("data", JSON.stringify(filteredData));
    formData.append("otherData", JSON.stringify(filteredOther));

    request("PUT", "setting/all-prefix", formData);
  };

  const feeType = watch("feeType");
  const blockWhenShipped = watch("blockWhenShipped");

  return (
    <div className="content  d-flex flex-column flex-column-fluid">
      <Breadcrumb
        title="Order Cancellation Setting"
        links={[
          { to: "/", name: "Dashboard" },
          { name: "Order Cancellation Setting" },
        ]}
      />

      <div className="d-flex flex-column-fluid">
        <div className="container">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>1. Cancellation Policy Text</div>
              <div style={sectionHelpStyle}>
                Customer app mein cancel se pehle ye terms dikhengi.
              </div>
              <div className="row">
                <div className="col-lg-6 mb-4 mb-lg-0">
                  <label style={labelStyle}>English</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    style={{ background: "#F3F6F9", borderColor: "#E4E6EF" }}
                    {...register("termsEn")}
                  />
                </div>
                <div className="col-lg-6">
                  <label style={labelStyle}>Arabic</label>
                  <textarea
                    className="form-control"
                    rows={5}
                    dir="rtl"
                    style={{ background: "#F3F6F9", borderColor: "#E4E6EF" }}
                    {...register("termsAr")}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <ReasonListEditor
                title="2. Cancellation Reasons (English)"
                help="Customer cancel karte time ye options select karega."
                reasons={reasonsEn}
                setReasons={setReasonsEn}
                placeholder="Type reason and click Add"
              />
              <ReasonListEditor
                title="2. Cancellation Reasons (Arabic)"
                help="Same reasons Arabic mein (same order best hai)."
                reasons={reasonsAr}
                setReasons={setReasonsAr}
                placeholder="السبب ثم Add"
                dir="rtl"
              />
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>3. When Cancel Is Not Allowed</div>
              <div style={sectionHelpStyle}>
                Select statuses jahan customer cancel nahi kar sakta. Shipped
                hone ke baad block alag se set karo.
              </div>

              <label style={labelStyle}>Blocked order statuses</label>
              <div className="row mb-6">
                {ORDER_STATUSES.map((status) => {
                  const active = blockedStatuses.includes(status.value);
                  return (
                    <div className="col-md-6 col-xl-3 mb-3" key={status.value}>
                      <button
                        type="button"
                        onClick={() => toggleStatus(status.value)}
                        className="btn btn-block text-left d-flex align-items-center"
                        style={{
                          ...(active ? chipActive : chipIdle),
                          borderRadius: "6px",
                          padding: "12px 14px",
                          fontWeight: 600,
                          fontSize: "13px",
                        }}
                      >
                        <span
                          className="d-inline-flex align-items-center justify-content-center mr-3"
                          style={{
                            width: "18px",
                            height: "18px",
                            borderRadius: "4px",
                            border: active
                              ? "1px solid #3699FF"
                              : "1px solid #B5B5C3",
                            background: active ? "#3699FF" : "#fff",
                            color: "#fff",
                            fontSize: "11px",
                            flexShrink: 0,
                          }}
                        >
                          {active ? "✓" : ""}
                        </span>
                        {status.label}
                      </button>
                    </div>
                  );
                })}
              </div>

              <label style={labelStyle}>
                Block when shipped / out for delivery?
              </label>
              <div className="d-flex flex-wrap">
                {[
                  {
                    value: "true",
                    label: "Yes, block after shipping",
                    help: "Tracking link add hone ke baad cancel band",
                  },
                  {
                    value: "false",
                    label: "No, still allow cancel",
                    help: "Shipping ke baad bhi cancel allow",
                  },
                ].map((option) => {
                  const active = blockWhenShipped === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className="btn text-left mr-3 mb-2"
                      style={{
                        ...(active ? chipActive : chipIdle),
                        borderRadius: "6px",
                        minWidth: "260px",
                        padding: "12px 16px",
                      }}
                      onClick={() =>
                        setValue("blockWhenShipped", option.value, {
                          shouldDirty: true,
                        })
                      }
                    >
                      <div style={{ fontWeight: 700, fontSize: "13px" }}>
                        {option.label}
                      </div>
                      <div style={{ fontSize: "12px", opacity: 0.8 }}>
                        {option.help}
                      </div>
                    </button>
                  );
                })}
              </div>
              <input type="hidden" {...register("blockWhenShipped")} />
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>4. Cancellation Charges</div>
              <div style={sectionHelpStyle}>
                Optional fee jo refund se deduct hogi. Default: No fee.
              </div>

              <label style={labelStyle}>Fee type</label>
              <div className="d-flex flex-wrap mb-5">
                {[
                  { value: "none", label: "No fee" },
                  { value: "fixed", label: "Fixed (SAR)" },
                  { value: "percent", label: "Percentage (%)" },
                ].map((option) => {
                  const active = feeType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className="btn mr-3 mb-2"
                      style={{
                        ...(active ? chipActive : chipIdle),
                        borderRadius: "6px",
                        minWidth: "140px",
                        fontWeight: 700,
                        fontSize: "13px",
                        padding: "10px 16px",
                      }}
                      onClick={() => {
                        setValue("feeType", option.value, { shouldDirty: true });
                        if (option.value === "none") setValue("feeValue", "0");
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <input type="hidden" {...register("feeType")} />

              {feeType !== "none" && (
                <div style={{ maxWidth: "280px" }}>
                  <label style={labelStyle}>
                    {feeType === "percent" ? "Percentage value" : "Amount (SAR)"}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    style={{ background: "#F3F6F9", borderColor: "#E4E6EF" }}
                    {...register("feeValue")}
                  />
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end pb-10">
              <button
                type="submit"
                className="btn btn-primary font-weight-bolder px-10 py-4"
              >
                Save Settings
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderCancellation;
